import React from 'react';
import { Input, FieldHint, FieldError } from '../styles/CustomerSpecsIndexStyles';
import {
  digitsOnly,
  validateUPC12,
  validateEAN13,
  preventNonDigitsOnKeyDown,
  handlePasteDigitsOnly,
} from '../utils/barcodeUtils';

// Single typed UPC/EAN code for the Customer Specs "Custom code" bypass (manual UPC plan).
// Digits-only input; the hard gate is a full UPC-A (12 + GS1 check) or EAN-13 (13 + check).
// An invalid entry shows a red outline (Input $error) + a red alert triangle with a hover
// tooltip; once valid, a muted line names the resolved symbology ("UPC-A" / "EAN-13").
// An empty value is allowed (UPC is optional) — the parent only blocks save when a non-empty
// value is invalid (see isValidManual).
export function isValidManual(value) {
  const d = digitsOnly(value);
  return validateUPC12(d) || validateEAN13(d);
}

// Red alert triangle with an exclamation mark. Uses currentColor (no hex / no var() in SVG
// attributes) so it inherits the danger color set on the wrapping span.
const AlertTriangle = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" aria-hidden="true" style={{ display: 'block' }}>
    <path d="M12 4.5 L20.5 19 H3.5 Z" fill="none" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
    <line x1="12" y1="10" x2="12" y2="13.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    <circle cx="12" cy="16.5" r="1.1" fill="currentColor" />
  </svg>
);

const ManualUpcField = ({ value, onChange }) => {
  const digits = digitsOnly(value);
  const isUpc = validateUPC12(digits);
  const isEan = validateEAN13(digits);
  const valid = isUpc || isEan;
  const empty = digits.length === 0;
  const showError = !empty && !valid;
  const wrongLength = digits.length !== 12 && digits.length !== 13;
  const tooltip = wrongLength
    ? 'Enter 12 digits (UPC-A) or 13 digits (EAN-13).'
    : 'Not a Valid UPC-A or EAN-13';

  const setDigits = (raw) => onChange(digitsOnly(raw).slice(0, 13));

  return (
    <div>
      <div style={{ position: 'relative', display: 'flex', maxWidth: 360 }}>
        <Input
          $error={showError}
          type="text"
          inputMode="numeric"
          autoComplete="off"
          aria-invalid={showError}
          aria-label="Custom UPC or EAN code"
          placeholder="Enter 12–13 digits"
          value={digits}
          maxLength={13}
          onKeyDown={preventNonDigitsOnKeyDown}
          onPaste={(e) => handlePasteDigitsOnly(e, setDigits)}
          onChange={(e) => setDigits(e.target.value)}
          style={{ width: '100%', paddingRight: showError ? 30 : undefined }}
        />
        {showError && (
          <span
            role="img"
            aria-label={tooltip}
            title={tooltip}
            style={{
              position: 'absolute',
              right: 9,
              top: '50%',
              transform: 'translateY(-50%)',
              color: 'var(--color-danger)',
              cursor: 'help',
            }}
          >
            <AlertTriangle />
          </span>
        )}
      </div>
      {valid && <FieldHint style={{ marginTop: 4 }}>{isUpc ? 'UPC-A' : 'EAN-13'}</FieldHint>}
      {showError && <FieldError style={{ marginTop: 4 }}>{tooltip}</FieldError>}
    </div>
  );
};

export default ManualUpcField;
